'use client'

import { useEffect, useState, type RefObject, type Dispatch, type SetStateAction } from 'react'
import { PlayerInterface } from '@foundation-player/api'
import { ga4Event } from '@/libs/Google/GoogleTagManager.lib'
import { getBreakpoint } from '@/utils/app/detect'
import { type VideoContext } from '@/types/cms'
import { type VideoPlayerProps, type VideoStickyState } from '@/types/video'

export const createPlayerProps = ({
  context,
  autoplay,
  heartbeatStarttype,
  isPurMode,
}: {
  context: VideoContext
  autoplay?: boolean
  heartbeatStarttype?: 0 | 1
  isPurMode: boolean
}): VideoPlayerProps => {
  const drm = context.model.fields.drm === 'true'

  return {
    id:
      context.model.fields['old-id'] && context.model.fields['old-id'].length > 0
        ? context.model.fields['old-id']
        : context.model.id,
    title: context.model.kicker + ': ' + context.model.title,
    publishedAt: context.model.publishedAt,
    lengthSeconds: context.model.durationMillis / 1000,
    mode: context.model.fields.mode,
    ...(context.model.image ? { poster: context.model.image.baseUrl + 'Img_16_9/920/image.webp' } : {}),
    ...(context.model.tags ? { keywords: context.model.tags } : {}),
    ...(context.model.fields['no-ads'] === 'true' || isPurMode ? { disableAds: true } : {}),
    trackingPath: context.model.meta.trackingPath,
    category: context.model.meta.videoAdZone,
    autoplay: autoplay ?? false,
    heartbeatStarttype: heartbeatStarttype ?? 0,
    source: {
      ...(!drm && context.model.video['web-prog'] ? { progressive: context.model.video['web-prog'] } : {}),
      ...(!drm && context.model.video['web-dash'] ? { dash: context.model.video['web-dash'] } : {}),
      ...(!drm && context.model.video['web-hls'] ? { hls: context.model.video['web-hls'] } : {}),
      ...(drm && context.model.video['web-drm-dash']
        ? { dash: context.model.video['web-drm-dash'] }
        : context.model.video['web-dash']
          ? { dash: context.model.video['web-dash'] }
          : {}),
      ...(drm && context.model.video['web-drm-hls']
        ? { hls: context.model.video['web-drm-hls'] }
        : context.model.video['web-hls']
          ? { hls: context.model.video['web-hls'] }
          : {}),
    },
  }
}

export const useTracking = (stickyState: VideoStickyState, category: string, videoId?: string) => {
  useEffect(() => {
    if (stickyState.trigger) {
      let action = ''

      if (stickyState.isSticky) {
        action = 'start'
      } else {
        switch (stickyState.trigger) {
          case 'close':
            action = 'closeByButton'
            break
          case 'scroll':
            action = 'closeByScroll'
            break
          case 'sessionEnd':
            action = 'closeByVideoEnd'
            break
        }
      }

      ga4Event({
        eventcategory: category,
        eventaction: action,
        eventlabel: window.location.href,
        eventvalue: videoId,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stickyState.isSticky])
}

export const useSticky = ({
  playerWrapper,
  player,
  isInlineTop,
}: {
  playerWrapper: RefObject<HTMLDivElement | null>
  player: PlayerInterface | undefined
  isInlineTop?: boolean
}): [VideoStickyState, Dispatch<SetStateAction<VideoStickyState>>] => {
  const [stickyState, setStickyState] = useState<VideoStickyState>({
    isSticky: false,
    trigger: undefined,
  })

  useEffect(() => {
    if (player) {
      document.addEventListener('scroll', () => {
        if (playerWrapper.current) {
          const rect = playerWrapper.current.getBoundingClientRect()
          setStickyState(
            rect.top < 40 &&
              (player.state.value.state === 'playing' ||
                (!isInlineTop && getBreakpoint() === 'xs' && !window.matchMedia('(orientation: landscape)').matches))
              ? { isSticky: true, trigger: 'scroll' }
              : { isSticky: false, trigger: 'scroll' },
          )
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player])

  return [stickyState, setStickyState]
}
